import { ApiError, staleTimes, useQuery } from '@platform/api-client';
import type { ResponseBody, ShippingClient, ShippingOperation } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { reportPeriodOf, type PeriodPreset } from '../report-period.ts';
import { pageParams, useKeysetPages } from '../useKeysetPages.ts';

export type HandoverList = ResponseBody<ShippingOperation<'/admin/handovers', 'get'>>;
export type Handover = HandoverList['items'][number];
export type HandoverDetail = ResponseBody<ShippingOperation<'/admin/handovers/{id}', 'get'>>;
export type HandoverParcel = HandoverDetail['parcels'][number];

const READY_PARCELS_SHOWN = 20;

export const HANDOVER_ROLE = 'orders:dispatch';

export const HANDOVER_STATUSES = ['open', 'closed'] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number];

export type HandoverFilters = {
  readonly carrier: string | null;
  readonly status: HandoverStatus | null;
  readonly period: PeriodPreset | null;
};

export const NO_HANDOVER_FILTERS: HandoverFilters = { carrier: null, status: null, period: null };

export type HandoverListing = HandoverFilters & { readonly search: string };

export const isHandoverListingNarrowed = (listing: HandoverListing): boolean =>
  listing.carrier !== null || listing.status !== null || listing.period !== null || listing.search !== '';

export const handoverQueryOf = (filters: HandoverListing, now: Date) => {
  const window = filters.period === null ? null : reportPeriodOf(filters.period, now);
  return {
    ...(filters.carrier === null ? {} : { carrier: filters.carrier }),
    ...(filters.status === null ? {} : { status: filters.status }),
    ...(window === null ? {} : { from: window.from, to: window.to }),
    ...(filters.search === '' ? {} : { search: filters.search }),
  };
};

const REFUSAL_REASONS = new Set(['closed', 'wrong_carrier', 'not_ready', 'empty', 'currency']);

export const refusalKeyOf = (error: unknown): string | null => {
  if (!(error instanceof ApiError) || error.code !== 'SHIPPING_HANDOVER_REFUSED') return null;
  const reason = error.details['reason'];
  return typeof reason === 'string' && REFUSAL_REASONS.has(reason) ? `handover.refused_${reason}` : null;
};

export const useHandovers = (shipping: ShippingClient, query: ReturnType<typeof handoverQueryOf>) => {
  const { tenantId } = usePageContext();
  return useKeysetPages<Handover>({
    queryKey: [...queryKeys.handovers(tenantId), 'list', query],
    resource: 'orders',
    fetchPage: (pageToken) =>
      shipping.get('/admin/handovers', { query: { ...query, ...pageParams(pageToken) } }),
  });
};

export const useHandover = (shipping: ShippingClient, handoverId: string) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.handover(tenantId, handoverId),
    queryFn: () => shipping.get('/admin/handovers/{id}', { path: { id: handoverId } }),
    staleTime: staleTimes.orders,
  });
};

export const useReadyParcels = (shipping: ShippingClient, handoverId: string, search: string) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.readyParcels(tenantId, handoverId, search),
    queryFn: () =>
      shipping.get('/admin/handovers/{id}/ready-parcels', {
        path: { id: handoverId },
        query: { search, page_size: READY_PARCELS_SHOWN },
      }),
    staleTime: staleTimes.orders,
  });
};
